import { css, styled } from 'styled-components';
import { ReactNode, useMemo } from 'react';

import { Breadcrumbs, Crumbs, Footer } from '@foradevelopment/fora-ui';
import { trimLeadingZeros } from '@fora-monorepo/string-utils';

import { layoutSizeSettings, SectionLayout } from '../Layout/SectionLayout';
import { BigHeader } from '../Header/BigHeader/BigHeader';
import { HeaderTitleData } from '../../common/utils';
import { SmallHeader } from '../Header/SmallHeader/SmallHeader';
import { useAppSelector } from '../../redux/hooks';
import { skipLink } from '../../router/links';

const PageWrapper = styled.div<{ print?: boolean }>`
  min-height: 100%;
  display: flex;
  flex-direction: column;

  ${props =>
    props.print &&
    css`
      @media print {
        // Hide content @ print to remove extra pages
        display: none;
      }
    `}
`;

type EmployerMyPagesLayoutProps = {
  headerTitleData: HeaderTitleData;
  hasPrintParam?: boolean;
  crumbs?: Crumbs;
  children: ReactNode;
};

export const EmployerMyPagesLayout = ({
  hasPrintParam,
  headerTitleData,
  crumbs,
  children,
}: EmployerMyPagesLayoutProps) => {
  const showBreadcrumbs = crumbs && crumbs.length > 1;

  const selectedPartnerNumberOrganization = useAppSelector(state => state.auth.user?.selectedPartnerNumberOrganization);
  const { organizationName, organizationNumber, agreementNumber, partnerNumber } = useAppSelector(
    state => state.employer.employerInfo
  );
  const orgDetails = useMemo(() => {
    return selectedPartnerNumberOrganization
      ? {
          organizationName,
          organizationNumber,
          agreementNumber: trimLeadingZeros(agreementNumber ?? ''),
          partnerNumber: trimLeadingZeros(partnerNumber),
        }
      : undefined;
  }, [agreementNumber, organizationName, organizationNumber, partnerNumber, selectedPartnerNumberOrganization]);

  return (
    <PageWrapper print={hasPrintParam}>
      {headerTitleData.subTitle ? (
        <BigHeader
          title={headerTitleData.title}
          subTitle={headerTitleData.subTitle}
          link={headerTitleData.link}
          hasSelectedEmployer={!!selectedPartnerNumberOrganization}
        />
      ) : (
        <SmallHeader
          orgDetails={orgDetails}
          title={headerTitleData.title}
          hasSelectedEmployer={!!selectedPartnerNumberOrganization}
        />
      )}
      {showBreadcrumbs && <Breadcrumbs crumbs={crumbs}></Breadcrumbs>}
      <SectionLayout
        flexGrow={1}
        paddingTop={!showBreadcrumbs ? 80 : undefined}
        paddingBottom={[80, 80, 120]}
        role="main"
        id={skipLink}
      >
        {children}
      </SectionLayout>
      <Footer
        maxWidth={layoutSizeSettings.maxWidth}
        paddingLeft={layoutSizeSettings.padding}
        paddingRight={layoutSizeSettings.padding}
      />
    </PageWrapper>
  );
};
